import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Menu, ShoppingBag, User, X } from "lucide-react";
import { useStore } from "@/store/StoreContext";
import { img } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home", id: "nav-home-link" },
  { to: "/shop", label: "Shop", id: "nav-shop-link" },
  { to: "/shop?drop=new", label: "New Drop", id: "nav-new-drop-link" },
  { to: "/collections", label: "Collections", id: "nav-collections-link" },
  { to: "/story", label: "Our Story", id: "nav-our-story-link" },
  { to: "/lookbook", label: "Lookbook", id: "nav-lookbook-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

export default function Nav() {
  const { cartCount, wishlist, setCartOpen, user } = useStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-noir-bg/80 border-b border-white/10" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button className="lg:hidden text-cream" onClick={() => setMenuOpen(true)} data-testid="nav-menu-open" aria-label="Open menu">
            <Menu size={20} strokeWidth={1.5} />
          </button>

          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src={img("logo-crest.png")} alt="Jae Noir crest" className="h-8 w-8 object-contain" />
            <span className="font-display font-extrabold tracking-[0.2em] text-sm sm:text-base uppercase">Jae Noir</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.id}
                className={({ isActive }) =>
                  `gold-underline font-mono2 text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${isActive && !l.to.includes("?") ? "text-gold active" : "text-cream/70 hover:text-cream"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <button onClick={() => navigate("/wishlist")} className="relative text-cream/80 hover:text-gold transition-colors duration-300" data-testid="nav-wishlist-trigger" aria-label="Wishlist">
              <Heart size={18} strokeWidth={1.5} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-noir-bg text-[9px] font-mono2 w-4 h-4 flex items-center justify-center rounded-full" data-testid="nav-wishlist-count">{wishlist.length}</span>
              )}
            </button>
            <button onClick={() => navigate("/account")} className={`transition-colors duration-300 ${user ? "text-gold" : "text-cream/80 hover:text-gold"}`} data-testid="nav-account-trigger" aria-label="Account">
              <User size={18} strokeWidth={1.5} />
            </button>
            <button onClick={() => setCartOpen(true)} className="relative text-cream/80 hover:text-gold transition-colors duration-300" data-testid="nav-cart-trigger" aria-label="Cart">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-noir-bg text-[9px] font-mono2 w-4 h-4 flex items-center justify-center rounded-full" data-testid="nav-cart-count">{cartCount}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[60] bg-noir-bg flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
          >
            <div className="h-16 px-4 flex items-center justify-between border-b border-white/10">
              <img src={img("logo-crest.png")} alt="" className="h-7 w-7 object-contain" />
              <span className="font-display font-extrabold tracking-[0.2em] text-sm uppercase">Jae Noir</span>
              <button onClick={() => setMenuOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <span key={l.id} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.7, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <NavLink
                      to={l.to}
                      onClick={() => setMenuOpen(false)}
                      data-testid={`mobile-${l.id}`}
                      className="font-display font-bold uppercase text-3xl py-2 text-cream/90 hover:text-gold transition-colors duration-300 block"
                    >
                      {l.label}
                    </NavLink>
                  </motion.span>
                </span>
              ))}
            </nav>
            <p className="px-8 pb-10 font-mono2 text-[10px] tracking-[0.35em] text-gold/70 uppercase">Move Different. Leave a Legacy.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
